import { useEffect, useState } from 'react'
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { getExpenses, getBudgets, getCustomCategories } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { DEFAULT_CATEGORIES, MODES, MODE_MAP, MONTHS } from '../lib/constants'
import { deleteExpense } from '../lib/supabase'

export default function SummaryPage() {
  const { user, household } = useAuth()
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year,  setYear]  = useState(now.getFullYear())
  const [expenses, setExp] = useState([])
  const [budgets,  setBud] = useState({})
  const [loading, setLoad] = useState(true)
  const [view, setView]   = useState('overview')
  const [allCats, setAllCats] = useState(DEFAULT_CATEGORIES)

  const load = async () => {
    setLoad(true)
    try {
      const [exps, buds, custom] = await Promise.all([
        getExpenses({ userId: user.id, householdId: null, month, year }),
        getBudgets(user.id, null),
        getCustomCategories(user.id)
      ])
      setExp(exps); setBud(buds)
      setAllCats([...DEFAULT_CATEGORIES, ...custom.map(c => ({ label: c.label, icon: c.icon, color: c.color }))])
    } catch(e) { console.error(e) }
    setLoad(false)
  }

  useEffect(() => { load() }, [month, year, household])

  const total   = expenses.reduce((s, e) => s + e.amount, 0)
  const days    = new Date(year, month, 0).getDate()
  const daysGone= month === now.getMonth()+1 && year === now.getFullYear() ? now.getDate() : days

  const knownCats = new Set(allCats.map(c => c.label))
  const expCats = [...new Set(expenses.map(e => e.category))]
    .filter(c => !knownCats.has(c))
    .map(c => ({ label: c, icon: '📦', color: '#95A5A6' }))
  const fullCats = [...allCats, ...expCats]

  const byCat = fullCats.map(c => ({
    name: c.label, icon: c.icon, color: c.color,
    amount: expenses.filter(e => e.category === c.label).reduce((s, e) => s + e.amount, 0),
    budget: budgets[c.label] || 0
  })).filter(c => c.amount > 0).sort((a, b) => b.amount - a.amount)

  const byMode = MODES.map(m => ({
    name: m.label, icon: m.icon, color: m.color,
    amount: expenses.filter(e => e.mode === m.label).reduce((s, e) => s + e.amount, 0)
  })).filter(m => m.amount > 0).sort((a, b) => b.amount - a.amount)

  const del = async (id) => {
    if (!window.confirm('Delete this expense?')) return
    await deleteExpense(id)
    load()
  }

  const downloadCSV = () => {
    const monthName = MONTHS[month-1]
    const rows = [
      ['PaisaTrack — ' + monthName + ' ' + year],
      ['Total spent', 'Rs.' + Math.round(total)],
      ['Transactions', expenses.length],
      ['Average per day', 'Rs.' + (daysGone ? Math.round(total/daysGone) : 0)],
      [],
      ['Date', 'Category', 'Payment Mode', 'Amount (Rs.)', 'Note'],
      ...expenses.map(e => [e.date, e.category, e.mode, Math.round(e.amount), e.note || ''])
    ]
    const csv = rows.map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'PaisaTrack_' + monthName + '_' + year + '.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  if (loading) return <div style={{ padding: '2rem', textAlign: 'center', color: '#888' }}>Loading…</div>

  return (
    <div style={{ padding: '1rem' }}>
      {/* Month selector */}
      <div style={s.monthRow}>
        <button style={s.arrow} onClick={() => { if (month === 1) { setMonth(12); setYear(y => y-1) } else setMonth(m => m-1) }}>‹</button>
        <span style={s.monthLabel}>{MONTHS[month-1]} {year}</span>
        <button style={s.arrow} onClick={() => { if (month === 12) { setMonth(1); setYear(y => y+1) } else setMonth(m => m+1) }}>›</button>
      </div>

      {/* Metric cards */}
      <div style={s.metricRow}>
        <div style={s.metric}><div style={s.metricVal}>₹{Math.round(total).toLocaleString('en-IN')}</div><div style={s.metricLbl}>Total spent</div></div>
        <div style={s.metric}><div style={s.metricVal}>{expenses.length}</div><div style={s.metricLbl}>Transactions</div></div>
        <div style={s.metric}><div style={s.metricVal}>₹{daysGone ? Math.round(total/daysGone).toLocaleString('en-IN') : 0}</div><div style={s.metricLbl}>Per day</div></div>
      </div>

      {/* Download CSV */}
      {expenses.length > 0 && (
        <button style={s.downloadBtn} onClick={downloadCSV}>
          ⬇ Download {MONTHS[month-1]} {year} as CSV
        </button>
      )}

      {/* Sub-tabs */}
      <div style={s.tabs}>
        {['overview','transactions'].map(v => (
          <button key={v} style={{ ...s.tab, ...(view===v ? s.tabActive : {}) }} onClick={() => setView(v)}>
            {v.charAt(0).toUpperCase()+v.slice(1)}
          </button>
        ))}
      </div>

      {view === 'overview' && expenses.length === 0 && (
        <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem 1rem' }}>No expenses this month yet.</div>
      )}

      {view === 'overview' && expenses.length > 0 && (<>
        {/* Pie chart */}
        <div style={s.card}>
          <div style={s.cardTitle}>Spending by category</div>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart><Pie data={byCat} dataKey="amount" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={50}>
              {byCat.map(c => <Cell key={c.name} fill={c.color} />)}
            </Pie><Tooltip formatter={v => '₹'+Math.round(v).toLocaleString('en-IN')} /></PieChart>
          </ResponsiveContainer>
          {byCat.map(c => {
            const pct = c.budget ? c.amount / c.budget : 0
            return (
              <div key={c.name} style={s.catRow}>
                <span style={s.catDot(c.color)}></span>
                <span style={{ flex: 1, fontSize: 14 }}>{c.icon} {c.name}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: pct >= 1 ? '#C0392B' : '#1A1A2E' }}>
                  ₹{Math.round(c.amount).toLocaleString('en-IN')}
                </span>
                {c.budget > 0 && (
                  <span style={{ fontSize: 12, color: pct >= 1 ? '#C0392B' : '#888', marginLeft: 8 }}>
                    / ₹{Math.round(c.budget).toLocaleString('en-IN')}
                  </span>
                )}
              </div>
            )
          })}
        </div>

        {/* Budget progress */}
        {Object.keys(budgets).length > 0 && (
          <div style={s.card}>
            <div style={s.cardTitle}>Budget progress</div>
            {allCats.filter(c => budgets[c.label]).map(c => {
              const spent = expenses.filter(e => e.category === c.label).reduce((sum, e) => sum + e.amount, 0)
              const pct = Math.min(spent / budgets[c.label], 1)
              const over = spent > budgets[c.label]
              return (
                <div key={c.label} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 5 }}>
                    <span>{c.icon} {c.label}</span>
                    <span style={{ color: over ? '#C0392B' : '#555', fontWeight: over ? 700 : 400 }}>
                      ₹{Math.round(spent).toLocaleString('en-IN')} / ₹{Math.round(budgets[c.label]).toLocaleString('en-IN')}
                      {over && ' ⚠'}
                    </span>
                  </div>
                  <div style={s.barTrack}>
                    <div style={{ ...s.barFill, width: `${pct*100}%`, background: over ? '#E74C3C' : c.color }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Payment mode bar chart */}
        <div style={s.card}>
          <div style={s.cardTitle}>By payment mode</div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={byMode} margin={{ left: -10 }}>
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '₹'+Math.round(v/1000)+'k'} />
              <Tooltip formatter={v => '₹'+Math.round(v).toLocaleString('en-IN')} />
              <Bar dataKey="amount" radius={[6,6,0,0]}>
                {byMode.map(m => <Cell key={m.name} fill={m.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </>)}

      {view === 'transactions' && (
        <div style={s.card}>
          <div style={s.cardTitle}>{expenses.length} transactions</div>
          {expenses.length === 0
            ? <p style={{ color: '#aaa', fontSize: 14 }}>No transactions yet.</p>
            : expenses.map(e => {
                const cat = [...DEFAULT_CATEGORIES, ...allCats].find(c => c.label === e.category) || { icon: '📦', color: '#95A5A6' }
                const mod = MODE_MAP[e.mode] || {}
                return (
                  <div key={e.id} style={s.txRow}>
                    <div style={{ ...s.txIcon, background: cat.color+'20', color: cat.color }}>{cat.icon}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{e.note || e.category}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>
                        {e.category} · {mod.icon} {e.mode}
                        {e.profiles?.name ? ` · ${e.profiles.name}` : ''}
                        {' · '}{e.date}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 15, fontWeight: 700 }}>₹{Math.round(e.amount).toLocaleString('en-IN')}</div>
                      <button onClick={() => del(e.id)} style={s.delBtn}>delete</button>
                    </div>
                  </div>
                )
              })
          }
        </div>
      )}
    </div>
  )
}

const s = {
  downloadBtn: { display: 'block', width: '100%', padding: '10px', marginBottom: 14, background: '#fff', border: '1.5px solid #E8C547', borderRadius: 10, fontSize: 14, fontWeight: 600, color: '#1A1A2E', cursor: 'pointer', textAlign: 'center' },
  monthRow:   { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16 },
  arrow:      { background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#555', padding: '0 4px' },
  monthLabel: { fontSize: 17, fontWeight: 700, color: '#1A1A2E', minWidth: 160, textAlign: 'center' },
  metricRow:  { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 },
  metric:     { background: '#1A1A2E', borderRadius: 14, padding: '14px 10px', textAlign: 'center' },
  metricVal:  { fontSize: 18, fontWeight: 700, color: '#E8C547' },
  metricLbl:  { fontSize: 11, color: '#888', marginTop: 4 },
  tabs:       { display: 'flex', gap: 4, background: '#F7F4EF', borderRadius: 10, padding: 4, marginBottom: 16 },
  tab:        { flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, background: 'transparent', color: '#888' },
  tabActive:  { background: '#fff', color: '#1A1A2E', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  card:       { background: '#fff', border: '1.5px solid #EEE', borderRadius: 16, padding: '1rem 1.25rem', marginBottom: 14 },
  cardTitle:  { fontSize: 13, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 },
  catRow:     { display: 'flex', alignItems: 'center', paddingTop: 8, borderTop: '1px solid #F5F5F5' },
  catDot:     c => ({ width: 10, height: 10, borderRadius: '50%', background: c, marginRight: 10, flexShrink: 0 }),
  barTrack:   { height: 8, background: '#F0F0F0', borderRadius: 4, overflow: 'hidden' },
  barFill:    { height: '100%', borderRadius: 4, transition: 'width 0.4s' },
  txRow:      { display: 'flex', alignItems: 'center', gap: 12, paddingTop: 12, borderTop: '1px solid #F5F5F5' },
  txIcon:     { width: 40, height: 40, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 },
  delBtn:     { background: 'none', border: 'none', color: '#E74C3C', fontSize: 11, cursor: 'pointer', padding: 0 },
}
